import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Divider,
    Alert
} from '@mui/material';
import {
    DragIndicator,
    Reorder,
    Save,
    Cancel,
    Add,
    Edit,
    Delete
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { putObject } from '../api/s3Api';
import { getS3Object } from '../utils/functions';
import { useOrgUser } from '../context/OrgUserProvider';

const CategoryManager = ({ open, onClose }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [editingName, setEditingName] = useState(null);
    const [newCategory, setNewCategory] = useState({
        name: '',
        description: '',
        isActive: true
    });
    const [error, setError] = useState('');
    const { getAccessTokenSilently } = useAuth0();
    const { orgUser } = useOrgUser();

    useEffect(() => {
        if (open) {
            fetchCategories();
            // Reset states when dialog opens
            setEditingName(null);
            setNewCategory({
                name: '',
                description: '',
                isActive: true
            });
            setError('');
        }
    }, [open]);

    const fetchCategories = async () => {
        try {
            const token = await getAccessTokenSilently({
                audience: 'https://qallix-api',
            });
            const orgName = orgUser.orgName;

            const response = await getS3Object(
                'QallixAcademy/SoftwareCourses/category.json',
                'qallix-public',
                token,
                orgName
            );

            if (response.statusCode === 200) {
                const categoryData = JSON.parse(atob(response.fileContent));
                setCategories(categoryData.categories || []);
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);    
            setError('Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (e, index) => {
        e.dataTransfer.setData('text/plain', index);
        setIsDragging(true);
        e.currentTarget.style.opacity = '0.5';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'));

        if (sourceIndex !== targetIndex) {
            const newCategories = [...categories];
            const [movedItem] = newCategories.splice(sourceIndex, 1);
            newCategories.splice(targetIndex, 0, movedItem);

            // Update order numbers based on new position
            const updatedCategories = newCategories.map((cat, index) => ({
                ...cat,
                order: index + 1
            }));

            setCategories(updatedCategories);
        }

        setIsDragging(false);
        e.currentTarget.style.opacity = '1';
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setIsDragging(false);
    };

    const handleSave = async () => {
        try {
            const token = await getAccessTokenSilently({
                audience: 'https://qallix-api',
            });
            const orgName = orgUser.orgName;

            const updatedData = {
                categories: categories
            };

            const jsonString = JSON.stringify(updatedData, null, 2);
            const base64Data = btoa(jsonString);

            const putData = {
                organization: orgName,
                putObjectItems: [
                    {
                        bucketName: 'qallix-public',
                        key: 'QallixAcademy/SoftwareCourses/category.json',
                        contentType: 'application/json',
                        fileContent: base64Data
                    }
                ]
            };

            await putObject(token, putData);
            onClose(true); // Pass true to indicate success
        } catch (error) {
            console.error('Failed to save categories:', error);
            setError('Failed to save changes. Please try again.');
        }
    };

    const handleAddCategory = () => {
        if (!newCategory.name.trim()) {
            setError('Category name is required');
            return;
        }

        // Check if category name already exists
        if (categories.some(cat => cat.name === newCategory.name)) {
            setError('Category name already exists');
            return;
        }

        const category = {
            name: newCategory.name,
            description: newCategory.description,
            isActive: newCategory.isActive,
            order: categories.length + 1
        };

        setCategories([...categories, category]);
        setNewCategory({
            name: '',
            description: '',
            isActive: true
        });
        setError('');
    };

    const handleEditCategory = (name) => {
        setEditingName(name);
        const category = categories.find(c => c.name === name);
        if (category) {
            setNewCategory({
                name: category.name,
                description: category.description,
                isActive: category.isActive
            });
        }
    };

    const handleUpdateCategory = () => {
        if (!newCategory.name.trim()) {
            setError('Category name is required');
            return;
        }

        // Check if the new name already exists (excluding the current category being edited)
        if (newCategory.name !== editingName &&
            categories.some(cat => cat.name === newCategory.name)) {
            setError('Category name already exists');
            return;
        }

        const updatedCategories = categories.map(cat =>
            cat.name === editingName
                ? {
                    name: newCategory.name,
                    description: newCategory.description,
                    isActive: newCategory.isActive,
                    order: cat.order // Keep the original order
                }
                : cat
        );

        setCategories(updatedCategories);
        setEditingName(null);
        setNewCategory({
            name: '',
            description: '',
            isActive: true
        });
        setError('');
    };

    const handleDeleteCategory = (name) => {
        const updatedCategories = categories
            .filter(cat => cat.name !== name)
            .map((cat, index) => ({ ...cat, order: index + 1 }));

        setCategories(updatedCategories);
    };

    const handleCancelEdit = () => {
        setEditingName(null);
        setNewCategory({
            name: '',
            description: '',
            isActive: true
        });
        setError('');
    };

    // Fix for boolean handling in Select component
    const handleStatusChange = (e) => {
        // Convert string value to boolean
        const isActive = e.target.value === 'true';
        setNewCategory({ ...newCategory, isActive });
    };


    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth sx={{ fontFamily: "'Satoshi', sans-serif" }}>
            <DialogTitle sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                <Box display="flex" alignItems="center" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                    <Reorder sx={{ mr: 1 }} />
                    Manage Categories
                </Box>
            </DialogTitle>
            <DialogContent sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Typography variant="h6" sx={{ mb: 2, fontFamily: "'Satoshi', sans-serif" }}>
                    {editingName ? 'Edit Category' : 'Add New Category'}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        flexWrap: 'wrap',
                        fontFamily: "'Satoshi', sans-serif",
                        alignItems: 'flex-start' // Align items at the top
                    }}>
                        <TextField
                            label="Category Name"
                            value={newCategory.name}
                            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                            required
                            sx={{ flexGrow: 1, minWidth: '200px' }} // Allow it to grow but maintain minimum width
                        />
                        <TextField
                            select
                            label="Status"
                            value={newCategory.isActive.toString()}
                            onChange={handleStatusChange}
                            sx={{
                                minWidth: 120,
                                alignSelf: 'flex-start' // Align to the top to match the Category Name field
                            }}
                        >
                            <MenuItem value="true">Active</MenuItem>
                            <MenuItem value="false">Inactive</MenuItem>
                        </TextField>
                    </Box>
                    <TextField
                        label="Description"
                        multiline
                        rows={2}
                        value={newCategory.description}
                        onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                        fullWidth // Make description field take full width
                    />
                    <Box sx={{ display: 'flex' }}>
                        {editingName ? (
                            <>
                                <Button
                                    onClick={handleUpdateCategory}
                                    variant="contained"
                                    sx={{ mr: 1, backgroundColor: '#332872', '&:hover': { backgroundColor: '#2c2458ff' } }}
                                >
                                    Update
                                </Button>
                                <Button
                                    onClick={handleCancelEdit}
                                    variant="outlined"
                                    sx={{ color: '#332872', borderColor: '#332872' }}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={handleAddCategory}
                                variant="contained"
                                startIcon={<Add />}
                                sx={{ backgroundColor: '#332872', '&:hover': { backgroundColor: '#2c2458ff' } }}
                            >
                                Add Category
                            </Button>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontFamily: "'Satoshi', sans-serif" }}>
                    Drag and drop categories to reorder them. Changes will be saved when you click Apply.
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, fontFamily: "'Satoshi', sans-serif" }}>
                    {categories.length === 0 ? (
                        <Typography variant="body1" sx={{ textAlign: 'center', py: 3, fontFamily: "'Satoshi', sans-serif" }}>
                            No categories yet. Add your first category above.
                        </Typography>
                    ) : (
                        categories.map((category, index) => (
                            <Paper
                                key={category.name}
                                elevation={1}
                                sx={{
                                    p: 2,
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'move',
                                    backgroundColor: isDragging ? 'action.hover' : 'background.paper',
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                        backgroundColor: 'action.hover'
                                    },
                                    fontFamily: "'Satoshi', sans-serif"
                                }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                            >
                                <DragIndicator sx={{ mr: 2, color: 'action.active' }} />
                                <Box flexGrow={1} sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                                    <Typography variant="subtitle1" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                                        {category.name}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                                        {category.description}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`Order: ${category.order}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontFamily: "'Satoshi', sans-serif", mr: 1 }}
                                />
                                <Chip
                                    label={category.isActive ? 'Active' : 'Inactive'}
                                    size="small"
                                    color={category.isActive ? 'success' : 'default'}
                                    sx={{ mr: 1, fontFamily: "'Satoshi', sans-serif" }}
                                />
                                <IconButton
                                    onClick={() => handleEditCategory(category.name)}
                                    size="small"
                                    sx={{ mr: 1 }}
                                >
                                    <Edit />
                                </IconButton>
                                <IconButton
                                    onClick={() => handleDeleteCategory(category.name)}
                                    size="small"
                                    color="error"
                                >
                                    <Delete />
                                </IconButton>
                            </Paper>
                        ))
                    )}
                </Paper>
            </DialogContent>
            <DialogActions sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                <Button
                    onClick={() => onClose(false)}
                    startIcon={<Cancel />}
                    sx={{
                        backgroundColor: '#332872',
                        color: 'white',
                        '&:hover': { backgroundColor: '#2c2458ff' },
                        fontFamily: "'Satoshi', sans-serif",
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={<Save />}
                    sx={{
                        backgroundColor: '#332872',
                        color: 'white',
                        '&:hover': { backgroundColor: '#2c2458ff' },
                        fontFamily: "'Satoshi', sans-serif",
                    }}
                >
                    Apply Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CategoryManager;